// src/pages/Dashboard/Admin/Messages/hooks/useMessagesDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  getAllMessages as getAllChatMessages,
  deleteMessage as deleteChatMessage,
} from "../../../../../services/chatServices";

import {
  getAllMessages as getAllPrivateMessages,
  deleteMessageById,
} from "../../../../../services/messageServices";

import { getGroupsHome } from "../../../../../services/groupServices";
import { getAdminsAndInstructorsIds } from "../../../../../services/userServices";

import {
  DEFAULT_MESSAGES_FILTERS,
  MESSAGE_SUBJECTS,
  calculateMessagesStats,
  filterUnifiedMessages,
  mapChatMessage,
  mapPrivateMessage,
  safeArray,
  sortMessagesByDate,
} from "../utils/messagesDashboardUtils";

export function useMessagesDashboard() {
  const [chatMessages, setChatMessages] = useState([]);
  const [privateMessages, setPrivateMessages] = useState([]);

  const [groups, setGroups] = useState([]);
  const [groupColors, setGroupColors] = useState({});
  const [adminInstructorIds, setAdminInstructorIds] = useState([]);

  const [loading, setLoading] = useState(true);
  const [deletingKey, setDeletingKey] = useState(null);

  const [filters, setFilters] = useState(DEFAULT_MESSAGES_FILTERS);

  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(false);
  const [detailsMessage, setDetailsMessage] = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);

      const [chatData, privateData, groupsData, idsData] = await Promise.all([
        getAllChatMessages(),
        getAllPrivateMessages(),
        getGroupsHome(),
        getAdminsAndInstructorsIds(),
      ]);

      const cleanPrivateMessages = safeArray(privateData).filter(
        (message) => !message?.is_copy
      );

      const colors = safeArray(groupsData).reduce((acc, group) => {
        if (group?.nom) acc[group.nom] = group.color_groupe;
        return acc;
      }, {});

      setChatMessages(safeArray(chatData));
      setPrivateMessages(cleanPrivateMessages);
      setGroups(safeArray(groupsData));
      setGroupColors(colors);
      setAdminInstructorIds(
        safeArray(idsData)
          .map((item) => item?.id)
          .filter(Boolean)
      );
    } catch (error) {
      console.error("Erreur chargement messages :", error);
      toast.error("Erreur lors du chargement des messages.");
      setChatMessages([]);
      setPrivateMessages([]);
      setGroups([]);
      setGroupColors({});
      setAdminInstructorIds([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const unifiedMessages = useMemo(() => {
    const chatRows = chatMessages.map((message) =>
      mapChatMessage(message, groupColors)
    );

    const privateRows = privateMessages.map(mapPrivateMessage);

    return sortMessagesByDate([...chatRows, ...privateRows]);
  }, [chatMessages, privateMessages, groupColors]);

  const filteredMessages = useMemo(() => {
    return filterUnifiedMessages(unifiedMessages, filters, adminInstructorIds);
  }, [unifiedMessages, filters, adminInstructorIds]);

  const stats = useMemo(
    () => calculateMessagesStats(unifiedMessages, adminInstructorIds),
    [unifiedMessages, adminInstructorIds]
  );

  const filteredStats = useMemo(
    () => calculateMessagesStats(filteredMessages, adminInstructorIds),
    [filteredMessages, adminInstructorIds]
  );

  const subjects = useMemo(() => {
    const existingSubjects = new Set(
      privateMessages.map((message) => message?.subject).filter(Boolean)
    );

    return Array.from(new Set([...MESSAGE_SUBJECTS, ...existingSubjects]));
  }, [privateMessages]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetFilters = () => {
    setFilters(DEFAULT_MESSAGES_FILTERS);
  };

  const openDetailsDrawer = (message) => {
    setDetailsMessage(message || null);
    setDetailsDrawerOpen(true);
  };

  const closeDetailsDrawer = () => {
    setDetailsDrawerOpen(false);
    setDetailsMessage(null);
  };

  const deleteMessage = async (message) => {
    if (!message?.key || !message?.id) return;

    const label =
      message.source === "chat"
        ? `le message du canal ${message.groupName || ""}`
        : `le message "${message.subject || "Autre"}"`;

    if (!window.confirm(`Supprimer définitivement ${label} ?`)) {
      return;
    }

    try {
      setDeletingKey(message.key);

      if (message.source === "chat") {
        await deleteChatMessage(message.id);
        setChatMessages((prev) => prev.filter((item) => item.id !== message.id));
      } else {
        await deleteMessageById(message.id);
        setPrivateMessages((prev) =>
          prev.filter((item) => item.id !== message.id)
        );
      }

      toast.success("Message supprimé avec succès.");

      if (detailsMessage?.key === message.key) {
        closeDetailsDrawer();
      }
    } catch (error) {
      console.error("Erreur suppression message :", error);
      toast.error("Erreur lors de la suppression du message.");
    } finally {
      setDeletingKey(null);
    }
  };

  return {
    chatMessages,
    privateMessages,
    unifiedMessages,
    filteredMessages,

    groups,
    groupColors,
    adminInstructorIds,
    subjects,

    stats,
    filteredStats,

    loading,
    deletingKey,

    filters,
    setFilter,
    resetFilters,

    detailsDrawerOpen,
    detailsMessage,
    openDetailsDrawer,
    closeDetailsDrawer,

    fetchAll,
    deleteMessage,
  };
}